'use client';
import { useAuth } from '@svenskaspelab/nova-core/auth/react/client';

import { INACTIVITY_SAFETY_MARGIN_MS, SESSION_END_MESSAGE_MS } from '../../constants.js';
import { useSessionTime } from '../../store/zustand.js';
import type { ShowDialogFn } from '../../types.js';
import { getUserSession } from './api.js';
import inactivityMessage from './dialogs/inactivity-message.js';
import { useInactivity } from './hooks/useInactivity.js';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

type InactivityMessageProps = {
  showDialog: ShowDialogFn;
  sessionEndMs?: number;
  throttle?: number;
};

export default function InactivityMessage({ showDialog, sessionEndMs, throttle }: InactivityMessageProps) {
  const { logout: authLogout } = useAuth();
  const sessionEndTime = useSessionTime((state) => state.sessionEndTime);
  const setIsShowingInactivityMessage = useSessionTime((state) => state.setIsShowingInactivityMessage);

  const logout = () => authLogout({ returnUrl: window.location.pathname + window.location.search });

  const handleInactivity = async () => {
    // False if close to session end time.
    const shouldShow = sessionEndTime.getTime() - Date.now() > 2 * INACTIVITY_SAFETY_MARGIN_MS;
    if (!shouldShow) {
      return;
    }
    setIsShowingInactivityMessage(true);
    await showDialog(
      inactivityMessage(
        () => void getUserSession(logout),
        () => void logout()
      )
    );
    setIsShowingInactivityMessage(false);
  };

  useInactivity({
    timeout: sessionEndMs ?? SESSION_END_MESSAGE_MS,
    onInactive: () => void handleInactivity(),
    onReset: () => void getUserSession(logout), // Keep alive session on user activity
    resetThrottle: throttle ?? FIVE_MINUTES_MS,
  });

  return null;
}
